

import React from 'react';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Contact = ({ content = {} }) => {
  const { language } = useLanguage();
  
  // Default fallback content
  const defaultContent = {
    contact: {
      phone: '+91 9321415838',
      email: 'admin@shreeservz.com'
    }
  };

  const t = content[language] || content.en || content.mr || defaultContent;

  return (
    <>
      <style>{`
        .contact-cta-section {
          padding: 80px 0;
          background: linear-gradient(-45deg, #c94b4b, #4b134f, #1e3a8a, #065f46);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          position: relative;
          overflow: hidden;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .contact-cta-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .contact-cta-section::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }

        .contact-cta-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 1;
        }

        .contact-cta-content {
          text-align: center;
          color: white;
        }

        .contact-cta-content h2 {
          font-size: 40px;
          margin-bottom: 16px;
          font-weight: 700;
        }

        .contact-cta-content p {
          font-size: 18px;
          margin-bottom: 40px;
          opacity: 0.95;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .contact-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 16px 32px;
          background: white;
          color: #667eea;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          text-decoration: none;
        }

        .cta-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
        }

        .cta-button-secondary {
          background: rgba(255, 255, 255, 0.15);
          color: white;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .cta-button-secondary:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        .contact-quick-info {
          display: flex;
          gap: 40px;
          justify-content: center;
          margin-top: 40px;
          flex-wrap: wrap;
        }

        .quick-info-item {
          display: flex;
          align-items: center;
          gap: 12px;
          color: white;
        }

        .quick-info-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .quick-info-text {
          text-align: left;
        }

        .quick-info-label {
          font-size: 12px;
          opacity: 0.9;
          margin-bottom: 4px;
        }

        .quick-info-value {
          font-size: 16px;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .contact-cta-section {
            padding: 60px 0;
          }

          .contact-cta-content h2 {
            font-size: 28px;
          }

          .contact-cta-content p {
            font-size: 16px;
          }

          .contact-cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-button {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .contact-quick-info {
            flex-direction: column;
            gap: 20px;
          }
        }
      `}</style>

      <section className="contact-cta-section">
        <div className="contact-cta-container">
          <div className="contact-cta-content">
            <h2>
              {language === 'mr' 
                ? 'मदत हवी आहे? आम्हाला संपर्क करा' 
                : 'Need Help? Get in Touch'}
            </h2>
            <p>
              {language === 'mr'
                ? 'आमची टीम तुम्हाला मदत करण्यासाठी तयार आहे. आता संपर्क साधा!'
                : 'Our team is ready to assist you with all your documentation needs'}
            </p>

            <div className="contact-cta-buttons">
              <a href="/contact" className="cta-button">
                {language === 'mr' ? 'संपर्क' : 'Visit Contact Page'}
                <ArrowRight size={20} />
              </a>
              <a href={`tel:${t?.contact?.phone || '+91 9321415838'}`} className="cta-button cta-button-secondary">
                <Phone size={20} />
                {language === 'mr' ? 'आता कॉल करा' : 'Call Now'}
              </a>
            </div>

            <div className="contact-quick-info">
              <div className="quick-info-item">
                <div className="quick-info-icon">
                  <Phone size={24} />
                </div>
                <div className="quick-info-text">
                  <div className="quick-info-label">
                    {language === 'mr' ? 'फोन' : 'Phone'}
                  </div>
                  <div className="quick-info-value">
                    {t?.contact?.phone || '+91 9321415838'}
                  </div>
                </div>
              </div>

              <div className="quick-info-item">
                <div className="quick-info-icon">
                  <Mail size={24} />
                </div>
                <div className="quick-info-text">
                  <div className="quick-info-label">
                    {language === 'mr' ? 'ईमेल' : 'Email'}
                  </div>
                  <div className="quick-info-value">
                    {t?.contact?.email || 'admin@shreeservz.com'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;


// import React from 'react';
// import { Phone, Mail, MapPin, Clock, MessageSquare } from 'lucide-react';

// const Contact = ({ language, content, onOpenPopup }) => {
//   const t = content[language];

//   const handleRequestService = () => {
//     // Call your existing popup function
//     if (onOpenPopup) {
//       onOpenPopup();
//     }
//   };

//   return (
//     <>
//       <style>{`
//         .contact-info-section {
//           padding: 80px 0;
//           background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
//         }

//         .contact-info-container {
//           max-width: 1200px;
//           margin: 0 auto;
//           padding: 0 20px;
//         }

//         .section-header {
//           text-align: center;
//           margin-bottom: 50px;
//         }

//         .section-header h2 {
//           font-size: 38px;
//           color: #1a1a1a;
//           margin-bottom: 12px;
//           font-weight: 700;
//         }

//         .section-header p {
//           font-size: 18px;
//           color: #666;
//           max-width: 600px;
//           margin: 0 auto;
//         }

//         .contact-info-grid {
//           display: grid;
//           grid-template-columns: repeat(2, 1fr);
//           gap: 24px;
//           margin-bottom: 40px;
//         }

//         .info-card {
//           background: white;
//           padding: 32px;
//           border-radius: 20px;
//           box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
//           transition: all 0.3s ease;
//           display: flex;
//           gap: 20px;
//           align-items: center;
//         }

//         .info-card:hover {
//           transform: translateY(-8px);
//           box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
//         }

//         .info-icon {
//           width: 70px;
//           height: 70px;
//           background: linear-gradient(135deg, #fff5f2 0%, #ffe5dc 100%);
//           border-radius: 16px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           flex-shrink: 0;
//           box-shadow: 0 8px 24px rgba(255, 107, 53, 0.15);
//         }

//         .info-content {
//           flex: 1;
//         }

//         .info-content h3 {
//           font-size: 16px;
//           color: #1a1a1a;
//           margin-bottom: 8px;
//           font-weight: 600;
//         }

//         .info-content p {
//           color: #666;
//           font-size: 16px;
//           line-height: 1.6;
//         }

//         .info-content a {
//           color: #667eea;
//           text-decoration: none;
//           transition: color 0.3s ease;
//         }

//         .info-content a:hover {
//           color: #4f5fd6;
//           text-decoration: underline;
//         }

//         .cta-section {
//           text-align: center;
//           padding: 50px;
//           background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
//           border-radius: 24px;
//           color: white;
//           position: relative;
//           overflow: hidden;
//         }

//         .cta-section::before {
//           content: '';
//           position: absolute;
//           top: -50%;
//           right: -10%;
//           width: 400px;
//           height: 400px;
//           background: rgba(255, 255, 255, 0.1);
//           border-radius: 50%;
//         }

//         .cta-section h3 {
//           font-size: 32px;
//           margin-bottom: 12px;
//           position: relative;
//           z-index: 1;
//         }

//         .cta-section p {
//           font-size: 18px;
//           margin-bottom: 30px;
//           opacity: 0.95;
//           position: relative;
//           z-index: 1;
//         }

//         .request-service-btn {
//           display: inline-flex;
//           align-items: center;
//           gap: 12px;
//           padding: 18px 36px;
//           background: white;
//           color: #667eea;
//           border: none;
//           border-radius: 12px;
//           font-size: 18px;
//           font-weight: 600;
//           cursor: pointer;
//           transition: all 0.3s ease;
//           box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
//           position: relative;
//           z-index: 1;
//         }

//         .request-service-btn:hover {
//           transform: translateY(-4px) scale(1.05);
//           box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
//         }

//         .request-service-btn:active {
//           transform: translateY(-2px) scale(1.02);
//         }

//         @media (max-width: 768px) {
//           .contact-info-section {
//             padding: 60px 0;
//           }

//           .section-header h2 {
//             font-size: 28px;
//           }

//           .section-header p {
//             font-size: 16px;
//           }

//           .contact-info-grid {
//             grid-template-columns: 1fr;
//             gap: 16px;
//           }

//           .info-card {
//             padding: 24px;
//           }

//           .info-icon {
//             width: 60px;
//             height: 60px;
//           }

//           .cta-section {
//             padding: 40px 20px;
//           }

//           .cta-section h3 {
//             font-size: 24px;
//           }

//           .cta-section p {
//             font-size: 16px;
//           }

//           .request-service-btn {
//             width: 100%;
//             justify-content: center;
//           }
//         }
//       `}</style>

//       <section className="contact-info-section">
//         <div className="contact-info-container">
//           <div className="section-header">
//             <h2>{language === 'mr' ? 'आमच्याशी संपर्क साधा' : 'Get in Touch'}</h2>
//             <p>
//               {language === 'mr' 
//                 ? 'तुमच्या सर्व दस्तऐवज आवश्यकतांसाठी आम्ही येथे आहोत'
//                 : 'We\'re here to help with all your documentation needs'}
//             </p>
//           </div>

//           <div className="contact-info-grid">
//             <div className="info-card">
//               <div className="info-icon">
//                 <MapPin color="#ff6b35" size={32} />
//               </div>
//               <div className="info-content">
//                 <h3>{language === 'mr' ? 'पत्ता' : 'Address'}</h3>
//                 <p>{t.contact.address}</p>
//               </div>
//             </div>

//             <div className="info-card">
//               <div className="info-icon">
//                 <Phone color="#ff6b35" size={32} />
//               </div>
//               <div className="info-content">
//                 <h3>{language === 'mr' ? 'फोन' : 'Phone'}</h3>
//                 <p>
//                   <a href={`tel:${t.contact.phone}`}>{t.contact.phone}</a>
//                 </p>
//               </div>
//             </div>

//             <div className="info-card">
//               <div className="info-icon">
//                 <Mail color="#ff6b35" size={32} />
//               </div>
//               <div className="info-content">
//                 <h3>{language === 'mr' ? 'ईमेल' : 'Email'}</h3>
//                 <p>
//                   <a href={`mailto:${t.contact.email}`}>{t.contact.email}</a>
//                 </p>
//               </div>
//             </div>

//             <div className="info-card">
//               <div className="info-icon">
//                 <Clock color="#ff6b35" size={32} />
//               </div>
//               <div className="info-content">
//                 <h3>{language === 'mr' ? 'कार्यालयीन वेळ' : 'Business Hours'}</h3>
//                 <p>{t.contact.hours}</p>
//               </div>
//             </div>
//           </div>

//           <div className="cta-section">
//             <h3>
//               {language === 'mr' 
//                 ? 'सेवा विनंती करा' 
//                 : 'Ready to Get Started?'}
//             </h3>
//             <p>
//               {language === 'mr'
//                 ? 'आता सेवा विनंती फॉर्म भरा आणि आम्ही लवकरच तुमच्याशी संपर्क साधू'
//                 : 'Fill out our service request form and we\'ll get back to you shortly'}
//             </p>
//             <button onClick={handleRequestService} className="request-service-btn">
//               <MessageSquare size={24} />
//               {language === 'mr' ? 'सेवा विनंती करा' : 'Request Service'}
//             </button>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// };

// export default Contact;